import type { FieldsProps } from './DestinationFields';
import { FieldMapEditor } from './FieldMapEditor';
import { FilingPreviewLine } from './FilingPreviewLine';
import { StorageConnection } from './StorageConnection';
import { StorageLocationPicker } from './StorageLocationPicker';
import { columnMetadata, type MetadataLabels } from './destination-kinds';
import type { PaigeDestination, PaigeDestinationsBridge, PaigeStorageKind } from './paige.global';

/** The sheet's hold on a storage destination: the destination as last read, and the connect actions. */
export interface StorageControls {
  current?: PaigeDestination;
  busy: boolean;
  connect: () => void;
  disconnect: () => void;
}

export interface StorageFieldsProps extends FieldsProps {
  kind: PaigeStorageKind;
  product: string;
  bridge: PaigeDestinationsBridge;
  storage: StorageControls;
  /** Where field values go: a library's columns when not given; null where the product has none. */
  metadata?: MetadataLabels | null;
  /** The picker's search placeholder; null where the provider has no search. */
  search: string | null;
}

/**
 * A storage destination's form (ADR-0016): the account it is connected as, the folder it files into
 * (chosen in the connected account once there is one), where the job's file naming puts the latest
 * document there, and the fields written as the product's metadata (a library's columns, Box
 * metadata). Folders and file names are the job's, not the destination's (Job setup, Export).
 */
/** Where the latest document would be filed, and whether its data file goes beside the PDF under the PDF's name (FilingSettings.dataFile). */
function FiledLines(props: Pick<StorageFieldsProps, 'kind' | 'bridge' | 'draft' | 'set'>) {
  const { kind, bridge, draft, set } = props;
  return (
    <>
      <FilingPreviewLine kind={kind} bridge={bridge} locationPath={draft.locationPath} />
      <label className="paige-check">
        <input
          type="checkbox"
          checked={draft.dataFile === 'yes'}
          onChange={(e) => set('dataFile')({ target: { value: e.target.checked ? 'yes' : '' } })}
        />
        Also file a data file (.json) beside each PDF
      </label>
    </>
  );
}

export function StorageFields(props: StorageFieldsProps) {
  const { draft, set, setMap, kind, bridge, storage, product, search } = props;
  const metadata = props.metadata === undefined ? columnMetadata : props.metadata;
  const connected = storage.current?.settings.connection?.state === 'connected';
  const choose = ({ id, path }: { id: string; path: string }) => {
    set('locationId')({ target: { value: id } });
    set('locationPath')({ target: { value: path } });
  };
  return (
    <>
      <StorageConnection
        current={storage.current}
        product={product}
        busy={storage.busy}
        onConnect={storage.connect}
        onDisconnect={storage.disconnect}
      />
      {storage.current && connected && (
        <StorageLocationPicker
          bridge={bridge}
          destinationId={storage.current.id}
          path={draft.locationPath}
          search={search}
          onChoose={choose}
        />
      )}
      <FiledLines kind={kind} bridge={bridge} draft={draft} set={set} />
      {metadata && <MetadataLines metadata={metadata} draft={draft} set={set} setMap={setMap} />}
    </>
  );
}

/**
 * Where field values go as the product's metadata: the choice to fill the columns or properties of the same names
 * (SharePoint, Google Drive; CapturePoint's words), and the lines that send a field elsewhere.
 */
function MetadataLines(
  props: Pick<StorageFieldsProps, 'draft' | 'set' | 'setMap'> & { metadata: MetadataLabels },
) {
  const { metadata, draft, set, setMap } = props;
  return (
    <>
      {metadata.sameNames && (
        <label className="paige-check">
          <input
            type="checkbox"
            checked={draft.sameNames === 'yes'}
            onChange={(e) => set('sameNames')({ target: { value: e.target.checked ? 'yes' : '' } })}
          />
          {metadata.sameNames}
        </label>
      )}
      {setMap && (
        <FieldMapEditor
          map={draft.fieldMap}
          onChange={setMap}
          targetLabel={metadata.targetLabel}
          legend={metadata.legend}
          hint={metadata.hint}
          emptyTarget={metadata.allByDefault ? 'Leave out' : 'Same name'}
        />
      )}
    </>
  );
}
